import _ from "lodash";

import {
  DEFAULT_EMPLOYMENT_SUBTYPE,
  DEFAULT_SALARY_INCOME_FREQUENCY,
  annualizeCashValue,
} from "lib/shared/compensation";
import { EmploymentSubType } from "lib/shared/compensation/employment/types";
import { SalaryIncomeFrequency, User, VariableInputMode } from "prisma/cm/client";

import { EMPLOYMENT_SUBTYPE_FULL_TIME } from "../compensation/employment/constants";
import { getAnnualizedProposedSalary } from "../compensation/pay-frequency";
import { getConvertedCurrency } from "../currency/getConvertedCurrency";
import { FieldProposal } from "../fields/types";
import { ReviewExchangeRates } from "../review/types";

export function getUserBonusTargetCents(user: User): number {
  if (user.bonusTargetInputMode === VariableInputMode.DOLLAR) {
    return user.bonusTargetNominal || 0;
  }
  if (user.bonusTargetInputMode === VariableInputMode.PERCENT) {
    const salaryCents = annualizeCashValue({
      value: user.salary || 0,
      incomeFrequency: user.salaryIncomeFrequency || DEFAULT_SALARY_INCOME_FREQUENCY,
      employmentSubType:
        (user.employmentSubType as EmploymentSubType) || DEFAULT_EMPLOYMENT_SUBTYPE,
    });

    const bonusTargetPercentInt = user.bonusTargetPercent || 0;
    const bonusTargetPercentDec = bonusTargetPercentInt / 100;
    return bonusTargetPercentDec * salaryCents;
  }

  throw Error("Invalid bonus target input mode");
}

export function getSumSalary(
  users: User[],
  {
    targetCurrency,
    exchangeRates,
  }: {
    targetCurrency: string;
    exchangeRates: ReviewExchangeRates;
  },
) {
  return _.sumBy(users, (user) => {
    const salary = user.salary;

    if (salary === null) return 0;

    const converted = getConvertedCurrency({
      value: salary,
      options: {
        sourceCurrency: user.salaryCurrencyCode,
        targetCurrency,
        exchangeRates,
      },
    });

    const annualized = annualizeCashValue({
      value: converted.raw,
      incomeFrequency: user.salaryIncomeFrequency,
      employmentSubType: (user.employmentSubType as EmploymentSubType) ?? null,
    });

    return _.round(annualized, 2);
  });
}

export function getBonusTargetInCents({
  bonusTargetNominal,
  bonusTargetPercent,
  bonusTargetInputMode,
  salary: base,
  salaryIncomeFrequency,
  employmentSubType,
}: Pick<
  User,
  | "bonusTargetNominal"
  | "bonusTargetPercent"
  | "bonusTargetInputMode"
  | "salary"
  | "salaryIncomeFrequency"
  | "employmentSubType"
>) {
  let bonusTarget;

  if (_.isNumber(bonusTargetNominal) && bonusTargetInputMode === VariableInputMode.DOLLAR) {
    bonusTarget = bonusTargetNominal ?? 0;
  } else {
    const salary = _.isNumber(base)
      ? base
      : annualizeCashValue({
          value: base ?? (0 as number),
          incomeFrequency: salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY,
          employmentSubType:
            (employmentSubType as EmploymentSubType) ?? EMPLOYMENT_SUBTYPE_FULL_TIME,
        });

    bonusTarget = (Number(bonusTargetPercent ?? 0) / 100) * salary;
  }
  // Note: return value is not converted, it is being returned in the currency which it is stored.
  return bonusTarget;
}

export const getBonusTargetInCentsForUser = (user: User, base: number | null = null) => {
  if (!_.isNumber(user.bonusTargetNominal) && !_.isNumber(user.bonusTargetPercent)) {
    return null;
  }
  return getBonusTargetInCents({
    bonusTargetNominal: user.bonusTargetNominal,
    bonusTargetPercent: user.bonusTargetPercent,
    bonusTargetInputMode: user.bonusTargetInputMode,
    salary: base,
    salaryIncomeFrequency: user.salaryIncomeFrequency,
    employmentSubType: user.employmentSubType,
  });
};

export const getBonusTargetInCentsForProposal = (proposal: FieldProposal) => {
  if (!_.isNumber(proposal.bonusTargetNominal) && !_.isNumber(proposal.bonusTargetPercent)) {
    return null;
  }
  const annualizedSalary = getAnnualizedProposedSalary(proposal);

  return getBonusTargetInCents({
    bonusTargetNominal: proposal.bonusTargetNominal,
    bonusTargetPercent: proposal.bonusTargetPercent,
    bonusTargetInputMode: proposal.bonusTargetInputMode ?? proposal.owner?.bonusTargetInputMode,
    salary: annualizedSalary,
    salaryIncomeFrequency: proposal.salaryIncomeFrequency ?? proposal.owner?.salaryIncomeFrequency,
    employmentSubType: proposal.owner?.employmentSubType,
  });
};
